import React, { useState, useEffect, useReducer, useContext } from "react";
import { toast } from "react-toastify";
import { useHistory } from "react-router-dom";

import { makeStyles } from "@material-ui/core/styles";
import Paper from "@material-ui/core/Paper";
import Button from "@material-ui/core/Button";
import Table from "@material-ui/core/Table";
import TableBody from "@material-ui/core/TableBody";
import TableCell from "@material-ui/core/TableCell";
import TableHead from "@material-ui/core/TableHead";
import TableRow from "@material-ui/core/TableRow";
import IconButton from "@material-ui/core/IconButton";
import SearchIcon from "@material-ui/icons/Search";
import TextField from "@material-ui/core/TextField";
import InputAdornment from "@material-ui/core/InputAdornment";
import Typography from "@material-ui/core/Typography";

import DeleteOutlineIcon from "@material-ui/icons/DeleteOutline";
import EditIcon from "@material-ui/icons/Edit";
import PeopleIcon from "@material-ui/icons/People";
import DownloadIcon from "@material-ui/icons/GetApp";
import CheckCircleIcon from "@material-ui/icons/CheckCircle";
import BlockIcon from "@material-ui/icons/Block";

import MainContainer from "../../components/MainContainer";
import MainHeader from "../../components/MainHeader";
import Title from "../../components/Title";

import api from "../../services/api";
import { i18n } from "../../translate/i18n";
import TableRowSkeleton from "../../components/TableRowSkeleton";
import Skeleton from "@material-ui/lab/Skeleton";
import ContactListDialog from "../../components/ContactListDialog";
import ConfirmationModal from "../../components/ConfirmationModal";
import toastError from "../../errors/toastError";
import { Grid, Box } from "@material-ui/core";
import Pagination from "@material-ui/lab/Pagination";

import planilhaExemplo from "../../assets/planilha.xlsx";
import ForbiddenPage from "../../components/ForbiddenPage";
import { AuthContext } from "../../context/Auth/AuthContext";
import * as XLSX from "xlsx";

const reducer = (state, action) => {
  if (action.type === "LOAD_CONTACTLISTS") {
    const contactLists = action.payload;
    const newContactLists = [];

    contactLists.forEach((contactList) => {
      const contactListIndex = state.findIndex((u) => u.id === contactList.id);
      if (contactListIndex !== -1) {
        state[contactListIndex] = contactList;
      } else {
        newContactLists.push(contactList);
      }
    });

    return [...state, ...newContactLists];
  }

  if (action.type === "LOAD_PAGE_CONTACTLISTS") {
    // Para paginação: substitui completamente os dados com os da página atual
    return action.payload;
  }

  if (action.type === "UPDATE_CONTACTLIST") {
    const contactList = action.payload;
    const contactListIndex = state.findIndex((u) => u.id === contactList.id);

    if (contactListIndex !== -1) {
      state[contactListIndex] = contactList;
      return [...state];
    } else {
      return [contactList, ...state];
    }
  }

  if (action.type === "DELETE_CONTACTLIST") {
    const contactListId = action.payload;

    const contactListIndex = state.findIndex((u) => u.id === contactListId);
    if (contactListIndex !== -1) {
      state.splice(contactListIndex, 1);
    }
    return [...state];
  }

  if (action.type === "RESET") {
    return [];
  }
};

const useStyles = makeStyles((theme) => ({
  mainPaper: {
    flex: 1,
    padding: theme.spacing(1),
    overflowY: "scroll",
    ...theme.scrollbarStyles,
  },
}));

const ContactLists = () => {
  const classes = useStyles();
  const history = useHistory();

  // Hooks SEMPRE no topo (sem if para "proteger")
  const { user, socket } = useContext(AuthContext);

  const [loading, setLoading] = useState(false);
  const [pageNumber, setPageNumber] = useState(1);
  const [hasMore, setHasMore] = useState(false);
  const [selectedContactList, setSelectedContactList] = useState(null);
  const [deletingContactList, setDeletingContactList] = useState(null);
  const [contactListModalOpen, setContactListModalOpen] = useState(false);
  const [confirmModalOpen, setConfirmModalOpen] = useState(false);
  const [searchParam, setSearchParam] = useState("");
  const [contactLists, dispatch] = useReducer(reducer, []);
  const [totalContactLists, setTotalContactLists] = useState(0);
  const [exporting, setExporting] = useState(false);
  const [exportStatus, setExportStatus] = useState("");
  
  // Configuração de paginação
  const itemsPerPage = 20;
  const skip = (pageNumber - 1) * itemsPerPage;

  // useEffect 1
  useEffect(() => {
    dispatch({ type: "RESET" });
    setPageNumber(1);
  }, [searchParam]);

  // useEffect 2
  useEffect(() => {
    setLoading(true);
    const delayDebounceFn = setTimeout(() => {
      const fetchContactLists = async () => {
        try {
          const { data } = await api.get("/contact-lists/", {
            params: { searchParam, take: pageNumber, skip: itemsPerPage },
          });
          // SEMPRE usar LOAD_PAGE_CONTACTLISTS para paginação tradicional
          // LOAD_CONTACTLISTS é apenas para scroll infinito (quando não há paginador)
          if (totalContactLists === 0) {
            // Scroll infinito: acumula dados
            dispatch({ type: "LOAD_CONTACTLISTS", payload: data.records });
          } else {
            // Paginação tradicional: substitui dados
            dispatch({ type: "LOAD_PAGE_CONTACTLISTS", payload: data.records });
          }
          setHasMore(data.hasMore);
          setTotalContactLists(data.count);
          setLoading(false);
        } catch (err) {
          toastError(err);
        }
      };
      fetchContactLists();
    }, 500);
    return () => clearTimeout(delayDebounceFn);
  }, [searchParam, pageNumber]);

  // useEffect 3
  useEffect(() => {
    const companyId = user.companyId;

    const onContactListEvent = (data) => {
      if (data.action === "update" || data.action === "create") {
        dispatch({ type: "UPDATE_CONTACTLIST", payload: data.record });
      }

      if (data.action === "delete") {
        dispatch({ type: "DELETE_CONTACTLIST", payload: +data.id });
      }
    };

    socket.on(`company-${companyId}-ContactList`, onContactListEvent);

    return () => {
      socket.off(`company-${companyId}-ContactList`, onContactListEvent);
    };
  }, [socket, user.companyId]);

  // SÓ AGORA: se o usuário for "user" e tiver "allowCampaigns" = "disabled",
  // retornamos a ForbiddenPage (sem condicionar os hooks acima)
  if (user.profile === "user" && user.allowCampaigns === "disabled") {
    return <ForbiddenPage />;
  }

  const handleOpenContactListModal = () => {
    setSelectedContactList(null);
    setContactListModalOpen(true);
  };

  const handleCloseContactListModal = () => {
    setSelectedContactList(null);
    setContactListModalOpen(false);
  };

  const handleSearch = (event) => {
    setSearchParam(event.target.value.toLowerCase());
  };

  // Função para exportar todos os contatos de todas as listas (apenas admin)
  const handleExportAllContacts = async () => {
    if (user.profile !== "admin") {
      toast.error("Acesso negado. Apenas administradores podem exportar todos os contatos.");
      return;
    }

    setExporting(true);
    setExportStatus("Preparando exportação...");

    try {
      let allContacts = [];
      let currentPage = 1;
      let hasMorePages = true;
      let totalProcessed = 0;
      let cursor = null; // Cursor para paginação (ID do último contato)
      const maxPages = 100; // Limite de páginas (500.000 contatos máx)
      const pageSize = 5000; // Tamanho por página (5 mil por vez)

      // Buscar páginas de contatos com limite máximo
      while (hasMorePages && currentPage <= maxPages) {
        setExportStatus(`Buscando página ${currentPage} de contatos...`);
        
        // Usar cursor se disponível (mais eficiente que OFFSET)
        const cursorParam = cursor ? `&cursor=${cursor}` : '';
        const response = await api.get(`contact-lists/export-all?page=${currentPage}&limit=${pageSize}${cursorParam}`);
        const { contacts, hasNextPage, nextCursor } = response.data;

        if (contacts.length === 0) {
          break;
        }

        // Adicionar contatos desta página
        allContacts = [...allContacts, ...contacts];
        totalProcessed += contacts.length;
        
        setExportStatus(`Processados ${totalProcessed.toLocaleString()} contatos...`);

        // Atualizar cursor para próxima página
        hasMorePages = hasNextPage;
        cursor = nextCursor;
        currentPage++;

        // Pausa entre requisições para servidor processar (1 segundo)
        if (hasMorePages && currentPage <= maxPages) {
          await new Promise(resolve => setTimeout(resolve, 1000));
        }
      }

      if (allContacts.length === 0) {
        toast.warn("Nenhum contato encontrado para exportar.");
        setExporting(false);
        setExportStatus("");
        return;
      }

      setExportStatus(`Removendo duplicatas de ${allContacts.length} contatos...`);

      // Remover duplicatas globais baseado no número
      const uniqueContacts = allContacts.filter((contact, index, self) => 
        index === self.findIndex(c => c.number === contact.number)
      );

      const duplicatesRemoved = allContacts.length - uniqueContacts.length;

      setExportStatus(`Gerando arquivo Excel com ${uniqueContacts.length} contatos únicos...`);

      // Mapear contatos para formato de exportação
      const exportData = uniqueContacts.map((contact) => ({
        "Nome": contact.name,
        "Número": contact.number,
        "WhatsApp": contact.whatsappValid,
        "Email": contact.email
      }));

      const wb = XLSX.utils.book_new();
      const ws = XLSX.utils.json_to_sheet(exportData);
      XLSX.utils.book_append_sheet(wb, ws, "Todos os Contatos");

      const fileName = `todos_contatos_${new Date().toISOString().split('T')[0]}.xlsx`;
      XLSX.writeFile(wb, fileName);

      const message = currentPage > maxPages 
        ? `Exportação parcial: ${uniqueContacts.length} contatos únicos exportados (limite de ${(maxPages * pageSize).toLocaleString()} contatos atingido)${duplicatesRemoved > 0 ? ` (${duplicatesRemoved} duplicatas removidas)` : ''}`
        : `Exportação concluída: ${uniqueContacts.length.toLocaleString()} contatos únicos exportados${duplicatesRemoved > 0 ? ` (${duplicatesRemoved} duplicatas removidas)` : ''}`;
       
      toast.success(message);
      
    } catch (err) {
      console.error("Erro ao exportar contatos:", err);
      toast.error("Erro ao exportar contatos. Tente novamente.");
    } finally {
      setExporting(false);
      setExportStatus("");
    }
  };

  const handleEditContactList = (contactList) => {
    setSelectedContactList(contactList);
    setContactListModalOpen(true);
  };

  const handleDeleteContactList = async (contactListId) => {
    try {
      await api.delete(`/contact-lists/${contactListId}`);
      toast.success(i18n.t("contactLists.toasts.deleted"));
    } catch (err) {
      toastError(err);
    }
    setDeletingContactList(null);
    setSearchParam("");
    setPageNumber(1);
  };

  // Removido loadMore() - não é necessário com paginação tradicional

  const handlePageChange = (event, value) => {
    setPageNumber(value);
  };

  const handleScroll = (e) => {
    // Desabilitar scroll infinito quando há paginação tradicional ativa
    // O scroll infinito só deve funcionar quando não há paginador visível
    if (!hasMore || loading || totalContactLists > 0) return;
    const { scrollTop, scrollHeight, clientHeight } = e.currentTarget;
    if (scrollHeight - (scrollTop + 100) < clientHeight) {
      setPageNumber((prevState) => prevState + 1);
    }
  };

  const goToContacts = (id) => {
    history.push(`/contact-lists/${id}/contacts`);
  };

  return (
    <MainContainer>
      <ConfirmationModal
        title={
          deletingContactList &&
          `${i18n.t("contactLists.confirmationModal.deleteTitle")} ${
            deletingContactList.name
          }?`
        }
        open={confirmModalOpen}
        onClose={setConfirmModalOpen}
        onConfirm={() => handleDeleteContactList(deletingContactList.id)}
      >
        {i18n.t("contactLists.confirmationModal.deleteMessage")}
      </ConfirmationModal>

      <ContactListDialog
        open={contactListModalOpen}
        onClose={handleCloseContactListModal}
        aria-labelledby="form-dialog-title"
        contactListId={selectedContactList && selectedContactList.id}
      />

      <MainHeader>
        <Grid style={{ width: "99.6%" }} container>
          <Grid xs={12} sm={8} item>
            <Title>{i18n.t("contactLists.title")}</Title>
          </Grid>
          <Grid xs={12} sm={4} item>
            <Grid spacing={2} container>
              <Grid xs={7} sm={6} item>
                <TextField
                  fullWidth
                  placeholder={i18n.t("contacts.searchPlaceholder")}
                  type="search"
                  value={searchParam}
                  onChange={handleSearch}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon style={{ color: "gray" }} />
                      </InputAdornment>
                    ),
                  }}
                />
              </Grid>
              <Grid xs={5} sm={6} item>
                <Button
                  fullWidth
                  variant="contained"
                  color="primary"
                  onClick={handleOpenContactListModal}
                >
                  {i18n.t("contactLists.buttons.add")}
                </Button>
              </Grid>
              {user.profile === "admin" && (
                <Grid xs={12} sm={12} item style={{ marginTop: "8px" }}>
                  <Button
                    fullWidth
                    variant="outlined"
                    color="secondary"
                    onClick={handleExportAllContacts}
                    disabled={exporting}
                    startIcon={<DownloadIcon />}
                  >
                    {exporting ? "Exportando..." : "Exportar Todos"}
                  </Button>
                </Grid>
              )}
            </Grid>
          </Grid>
        </Grid>
      </MainHeader>

      {exportStatus && (
        <Box style={{ padding: "16px", textAlign: "center", backgroundColor: "#f5f5f5", marginBottom: "16px", borderRadius: "4px" }}>
          <Typography variant="body2" color="textSecondary">
            {exportStatus}
          </Typography>
        </Box>
      )}

      {loading ? (
        <Paper className={classes.mainPaper} variant="outlined">
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell align="center">Status</TableCell>
                <TableCell align="center">
                  {i18n.t("contactLists.table.name")}
                </TableCell>
                <TableCell align="center">
                  {i18n.t("contactLists.table.owner")}
                </TableCell>
                <TableCell align="center">
                  {i18n.t("contactLists.table.contacts")}
                </TableCell>
                <TableCell align="center">
                  {i18n.t("contactLists.table.actions")}
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
               {[...Array(20)].map((_, index) => (
                <TableRow key={index}>
                  <TableCell align="center">
                    <IconButton size="small" disabled>
                      <Skeleton variant="circle" width={24} height={24} />
                    </IconButton>
                  </TableCell>
                  <TableCell align="center">
                    <Skeleton variant="text" width="100%" />
                  </TableCell>
                  <TableCell align="center">
                    <Skeleton variant="text" width="100%" />
                  </TableCell>
                  <TableCell align="center">
                    <Skeleton variant="text" width="100%" />
                  </TableCell>
                  <TableCell align="center">
                    <Box display="flex" justifyContent="center" gap={1}>
                      <Skeleton variant="circle" width={32} height={32} />
                      <Skeleton variant="circle" width={32} height={32} />
                      <Skeleton variant="circle" width={32} height={32} />
                      <Skeleton variant="circle" width={32} height={32} />
                    </Box>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Paper>
      ) : (
        <Paper
          className={classes.mainPaper}
          variant="outlined"
          onScroll={handleScroll}
        >
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell align="center">Status</TableCell>
                <TableCell align="center">
                  {i18n.t("contactLists.table.name")}
                </TableCell>
                <TableCell align="center">
                  {i18n.t("contactLists.table.owner")}
                </TableCell>
                <TableCell align="center">
                  {i18n.t("contactLists.table.contacts")}
                </TableCell>
                <TableCell align="center">
                  {i18n.t("contactLists.table.actions")}
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {contactLists.map((contactList) => (
                <TableRow key={contactList.id}>
                  <TableCell align="center">
                    <IconButton size="small">
                      {!contactList.isValid ? (
                        <BlockIcon
                          htmlColor="red"
                          title={contactList.contactsCount === 0 ? "Lista vazia" : "Contatos inválidos"}
                        />
                      ) : (
                        <CheckCircleIcon
                          htmlColor="green"
                          title="Lista válida"
                        />
                      )}
                    </IconButton>
                  </TableCell>
                  <TableCell align="center">{contactList.name}</TableCell>
                  <TableCell align="center">
                    {contactList.user ? contactList.user.name : "Não definido"}
                  </TableCell>
                  <TableCell align="center">
                    {contactList.contactsCount || 0}
                  </TableCell>
                  <TableCell align="center">
                    <a href={planilhaExemplo} download="planilha.xlsx">
                      <IconButton size="small" title="Baixar Planilha Exemplo">
                        <DownloadIcon />
                      </IconButton>
                    </a>

                    <IconButton
                      size="small"
                      onClick={() => goToContacts(contactList.id)}
                    >
                      <PeopleIcon />
                    </IconButton>

                    <IconButton
                      size="small"
                      onClick={() => handleEditContactList(contactList)}
                    >
                      <EditIcon />
                    </IconButton>

                    <IconButton
                      size="small"
                      onClick={() => {
                        setConfirmModalOpen(true);
                        setDeletingContactList(contactList);
                      }}
                    >
                      <DeleteOutlineIcon />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Paper>
      )}
      
      {totalContactLists > 0 && (
        <Box display="flex" justifyContent="center" mt={2} mb={2}>
          <Pagination
            count={Math.ceil(totalContactLists / itemsPerPage)}
            page={pageNumber}
            onChange={handlePageChange}
            color="primary"
            showFirstButton
            showLastButton
          />
        </Box>
      )}
    </MainContainer>
  );
};

export default ContactLists;